import { MemoryRouter } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { usePunditUserContext } from "@circle-react/contexts";
import { Modal } from "@circle-react-uikit/ModalV2";
import { ContactSupport } from "./ContactSupport";
import { Loader } from "./Loader";
import { UpgradeFlow } from "./UpgradeFlow";
import type { UpgradePlanProviderProps } from "./UpgradePlanContext";
import {
  UpgradePlanProvider,
  useUpgradePlanContext,
} from "./UpgradePlanContext";

const i18nRoot = "pricing_plans.upgrade_modal";

export interface UpgradePlanModalComponentProps {
  onCloseModal?: () => void | Promise<void>;
  onSuccess?: () => void | Promise<void>;
  subheading?: string;
  successButtonLabel?: string;
}

// we need to support 2 different flows for trials
// 1. from workflows we show the plan details (showPlanDetails = true)
// 2. from plans page we redirect to stripe checkout session immediately
// (showPlanDetails = false)

// Todo: Missing translations for Pro and Basic when showPlanDetails = true
const UpgradePlanModalComponent = ({
  subheading,
  onSuccess,
  successButtonLabel,
  onCloseModal,
}: UpgradePlanModalComponentProps) => {
  const {
    isPlanLoading,
    isEmailHubPlanLoading,
    errorFetchingEmailHubPlan,
    errorFetchingPlan,
    isEmailHubRequired,
  } = useUpgradePlanContext();

  const { refetch: refetchPundit } = usePunditUserContext();
  const modal = useModal();

  const handleOnSuccess = async () => {
    await refetchPundit();

    void modal.hide();
    modal.remove();
    if (isEmailHubRequired) {
      // [TODO][EmailHubBilling][AfterRelease] Remove this and diagnose the actual issue
      window.location.reload();
      return;
    }
    if (onSuccess) {
      await onSuccess();
    }
  };

  const handleOnClose = async () => {
    await refetchPundit();

    if (onCloseModal) {
      await onCloseModal();
    }
    await modal.hide();
    modal.remove();
  };

  return (
    <Modal isOpen={modal.visible} onClose={handleOnClose}>
      <Modal.Overlay />
      <Modal.Content size="md" className="!max-h-[85vh] !w-[35.25rem]">
        {isPlanLoading || isEmailHubPlanLoading ? (
          <Loader title={t([i18nRoot, "loading.plan"])} />
        ) : errorFetchingPlan || errorFetchingEmailHubPlan ? (
          <ContactSupport
            title={t([i18nRoot, "errors.plan_not_found.title"])}
            errorMessage={t([i18nRoot, "errors.plan_not_found.message"])}
          />
        ) : (
          <UpgradeFlow
            subheading={subheading}
            successButtonLabel={successButtonLabel}
            handleOnSuccess={handleOnSuccess}
            modal={modal}
          />
        )}
      </Modal.Content>
    </Modal>
  );
};

export interface UpgradePlanModalProps
  extends Omit<UpgradePlanProviderProps, "children">,
    UpgradePlanModalComponentProps {
  isRenewal?: boolean;
  shouldSkipProrationStep?: boolean;
}

const UpgradePlanModal = ModalManager.create(
  ({
    planTier,
    subheading,
    source,
    onSuccess,
    successButtonLabel,
    usedForWorkflows,
    isEmailHubRequired,
    showPlanDetails = false,
    couponId,
    planId,
    onCloseModal,
    shouldSkipProrationStep = false,
    isRenewal = false,
  }: UpgradePlanModalProps) => (
    <MemoryRouter>
      <UpgradePlanProvider
        planTier={planTier}
        planId={planId}
        couponId={couponId}
        source={source}
        usedForWorkflows={usedForWorkflows}
        isEmailHubRequired={isEmailHubRequired}
        showPlanDetails={showPlanDetails}
        shouldSkipProrationStep={shouldSkipProrationStep}
        isRenewal={isRenewal}
      >
        <UpgradePlanModalComponent
          subheading={subheading}
          onSuccess={onSuccess}
          successButtonLabel={successButtonLabel}
          onCloseModal={onCloseModal}
        />
      </UpgradePlanProvider>
    </MemoryRouter>
  ),
);

export const useUpgradePlanModal = () => useModal(UpgradePlanModal);
